import type { RazorpayProviderError } from "@/lib/razorpay-custom-checkout";
import { isPaymentOutcomeUnknown, toFriendlyPaymentMessage } from "./messages";

export type CardErrorField = "name" | "number" | "expiry" | "cvv";

export type CardPaymentFeedback = {
  code: string;
  message: string;
  action: "edit_card" | "retry_verification" | "choose_method" | "check_status";
  field?: CardErrorField;
};

/** Stable diagnostic categories and user copy; never return raw card data. */
export function getCardPaymentFeedback(failure: {
  message: string;
  providerError?: RazorpayProviderError;
}): CardPaymentFeedback {
  const reason = failure.providerError?.reason?.toLowerCase() ?? "";
  const description = failure.message.toLowerCase();
  if (isPaymentOutcomeUnknown(failure.message)) {
    return {
      code: "status_unknown",
      message: toFriendlyPaymentMessage(failure.message),
      action: "check_status",
    };
  }
  if (
    /card_number_invalid|invalid_card_number/.test(reason) ||
    /invalid card number|card number is invalid/.test(description)
  ) {
    return {
      code: "invalid_card_number",
      message:
        "Check the card number and enter it again. You can also use another card or UPI.",
      action: "edit_card",
      field: "number",
    };
  }
  if (
    /expired_card|card_expired|expiry|expiration/.test(reason) ||
    /card.*expired|invalid.*expiry/.test(description)
  ) {
    return {
      code: "invalid_card_expiry",
      message:
        "Check the expiry date on your card. If the card has expired, use another card or UPI.",
      action: "edit_card",
      field: "expiry",
    };
  }
  if (
    /cvv|cvc/.test(reason) ||
    /invalid.*cvv|incorrect.*cvv/.test(description)
  ) {
    return {
      code: "invalid_card_cvv",
      message: "Check the security code on your card and enter it again.",
      action: "edit_card",
      field: "cvv",
    };
  }
  if (/incorrect_card_details|card_details_invalid/.test(reason)) {
    return {
      code: "incorrect_card_details",
      message:
        "The card details weren't accepted. Check the card number, expiry date and security code, or use another method.",
      action: "edit_card",
    };
  }
  if (
    /invalid_otp|otp_expired|authentication_failed|card_not_enrolled/.test(
      reason,
    )
  ) {
    return {
      code: "card_verification_failed",
      message:
        "Your bank couldn't verify this payment. Try again and complete the bank's OTP or approval step, or use another method.",
      action: "retry_verification",
    };
  }
  if (/insufficient_funds/.test(reason)) {
    return {
      code: "insufficient_funds",
      message:
        "Your bank reported insufficient funds. Use another card or UPI, or check your available balance before trying again.",
      action: "choose_method",
    };
  }
  if (
    /card_not_supported|card_network_not_enabled|international_transaction_not_allowed|card_not_enabled|transaction_not_allowed/.test(
      reason,
    )
  ) {
    return {
      code: "card_not_supported",
      message:
        "This card can't be used for this payment. Try another card or UPI, or ask your bank whether online payments are enabled.",
      action: "choose_method",
    };
  }
  if (/payment_timed_out|payment_cancelled/.test(reason)) {
    return {
      code: "card_verification_incomplete",
      message:
        "The bank reported that this payment wasn't completed. Try again and finish its verification step. If your account was debited, check the payment status first.",
      action: "retry_verification",
    };
  }
  if (
    /gateway_technical_error|bank_technical_error|bank_unavailable|issuer_unavailable/.test(
      reason,
    )
  ) {
    return {
      code: "bank_unavailable",
      message:
        "The bank couldn't process this payment right now. Try another card or UPI, or try again later.",
      action: "choose_method",
    };
  }
  if (
    /card_declined|payment_declined|payment_risk_check_failed|payment_failed/.test(
      reason,
    )
  ) {
    return {
      code: "card_declined",
      message:
        "Your bank declined this payment. Try another card or UPI. If it keeps happening, ask your bank why the payment was declined.",
      action: "choose_method",
    };
  }
  return {
    code: "card_payment_unavailable",
    message:
      "We couldn't complete this card payment. Try another card or UPI. If your account was debited, check the payment status before paying again.",
    action: "choose_method",
  };
}

export function cardFeedbackProperties(feedback: CardPaymentFeedback) {
  return {
    payment_feedback_code: feedback.code,
    recovery_action: feedback.action,
    invalid_field: feedback.field ?? null,
    payment_outcome: feedback.action === "check_status" ? "unknown" : "failed",
  };
}
